import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Plus, TrendingUp, BarChart3, FileText, ArrowRight } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const BudgetingForecasting = () => {
  const budgetFeatures = [
    {
      id: 'create',
      title: 'Create Budget Plan',
      description: 'Design comprehensive budget plans for your organization.',
      icon: <Plus size={24} />,
      path: '/finance/budgeting/create',
      color: 'bg-green-50 text-green-600'
    },
    {
      id: 'forecast',
      title: 'Forecast Analysis',
      description: 'Advanced forecasting models for financial planning.',
      icon: <TrendingUp size={24} />,
      path: '/finance/budgeting/forecast',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      id: 'variance',
      title: 'Budget vs Actuals',
      description: 'Compare budgeted vs actual performance with variance analysis.',
      icon: <BarChart3 size={24} />,
      path: '/finance/budgeting/variance',
      color: 'bg-orange-50 text-orange-600'
    },
    {
      id: 'reports',
      title: 'Budget Reports',
      description: 'Comprehensive reports and analytics for budget management.',
      icon: <FileText size={24} />,
      path: '/finance/budgeting/reports',
      color: 'bg-purple-50 text-purple-600'
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Budget & Forecast Management"
        description="Comprehensive budgeting and forecasting tools to plan, track, and optimize your financial performance"
      />
      <div className="page-container" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>

      {/* Features Grid */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Budget Management Tools</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Powerful tools for creating, managing, and analyzing budgets and forecasts
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {budgetFeatures.map((feature) => (
              <Link
                key={feature.id}
                to={feature.path}
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-green-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg ${feature.color}`}>
                    {feature.icon}
                  </div>
                  <ArrowRight 
                    size={20} 
                    className="text-gray-400 group-hover:text-green-600 transition-colors" 
                  />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Start Your Budget Planning
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Create comprehensive budgets and forecasts to drive your financial success.
          </p>
          <Link
            to="/finance/budgeting/create"
            className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus size={20} className="mr-2" />
            Create Budget Plan
          </Link>
        </div>
      </div>
      </div>
    </>
  );
};

export default BudgetingForecasting;
